import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Radio, Users, ToggleRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { AccountModules, saveAccountModules, useAccountModules } from '../../services/accountModulesService';

// Configurações → Módulos: liga/desliga telas da conta (igual aos módulos do CRM).
export default function ModulesSettings() {
  const { t } = useTranslation();
  const mods = useAccountModules();
  const [saving, setSaving] = useState<keyof AccountModules | null>(null);

  const items: { key: keyof AccountModules; icon: React.ElementType; title: string; desc: string }[] = [
    {
      key: 'liveAutomation',
      icon: Radio,
      title: t('modules.liveAutomation.title', 'Live Automation'),
      desc: t('modules.liveAutomation.desc', 'Automação de vendas durante lives (Instagram/YouTube).'),
    },
    {
      key: 'contacts',
      icon: Users,
      title: t('modules.contacts.title', 'Contatos e segmentação'),
      desc: t('modules.contacts.desc', 'Lista de leads e segmentos para usar nas landing pages e campanhas pagas.'),
    },
  ];

  const toggle = async (key: keyof AccountModules) => {
    if (!mods) return;
    setSaving(key);
    try {
      await saveAccountModules({ [key]: !mods[key] });
      toast.success(t('modules.saved', 'Módulo atualizado'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('modules.error', 'Não foi possível salvar'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <Card>
        <CardContent>
          <div className="flex items-start gap-4 py-2">
            <div className="rounded-2xl bg-gradient-to-br from-primary-500/10 to-purple-500/10 p-3 dark:from-primary-500/20 dark:to-purple-500/20">
              <ToggleRight className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('modules.title', 'Módulos')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('modules.subtitle', 'Escolha o que aparece no menu. Desligar um módulo não apaga nenhum dado.')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map(({ key, icon: Icon, title, desc }) => {
              const on = !!mods?.[key];
              return (
                <div key={key} className="flex items-center gap-4 py-4">
                  <Icon className="h-6 w-6 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    disabled={!mods || saving === key}
                    onClick={() => toggle(key)}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${on ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
